import React, { useState } from 'react';
import { motion } from 'framer-motion';
import MainLayout from '../components/layout/MainLayout';
import { useTheme } from '../context/ThemeContext';
import {
  TrendingUp, TrendingDown, BarChart3, PieChart as PieIcon,
  Calendar, Users, Briefcase, IndianRupee, Target, Award
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, LineChart, Line
} from 'recharts';

const revenueData = [
  { month: 'Jul', revenue: 180000, target: 150000 },
  { month: 'Aug', revenue: 220000, target: 180000 },
  { month: 'Sep', revenue: 195000, target: 200000 },
  { month: 'Oct', revenue: 280000, target: 220000 },
  { month: 'Nov', revenue: 310000, target: 250000 },
  { month: 'Dec', revenue: 290000, target: 280000 },
];

const caseDistribution = [
  { name: 'Civil', value: 35, color: '#2D8CFF' },
  { name: 'Criminal', value: 25, color: '#D4AF37' },
  { name: 'Corporate', value: 20, color: '#10B981' },
  { name: 'Family', value: 12, color: '#8B5CF6' },
  { name: 'Other', value: 8, color: '#F59E0B' },
];

const performanceData = [
  { metric: 'Cases Won', value: 78, target: 75 },
  { metric: 'Client Retention', value: 92, target: 85 },
  { metric: 'On-Time Delivery', value: 88, target: 90 },
  { metric: 'Client Satisfaction', value: 95, target: 90 },
];

const Analytics = () => {
  const { isDark } = useTheme();
  const [period, setPeriod] = useState('6months');

  return (
    <MainLayout title="Analytics" subtitle="Insights and performance metrics">
      {/* Period Selector */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-1 p-1 rounded-xl glass-card">
          {[{ label: '1 Month', value: '1month' }, { label: '6 Months', value: '6months' }, { label: '1 Year', value: '1year' }, { label: 'All Time', value: 'all' }].map((p) => (
            <motion.button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                period === p.value ? 'bg-gradient-to-r from-[#1E3A5F] to-[#2D8CFF] text-white' : ''
              }`}
              whileTap={{ scale: 0.95 }}
            >
              {p.label}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        {[
          { label: 'Total Revenue', value: '₹14.8L', change: '+18%', trend: 'up', icon: IndianRupee, color: 'green' },
          { label: 'Cases Handled', value: '156', change: '+24', trend: 'up', icon: Briefcase, color: 'blue' },
          { label: 'Win Rate', value: '78%', change: '+5%', trend: 'up', icon: Award, color: 'purple' },
          { label: 'Active Clients', value: '89', change: '+12', trend: 'up', icon: Users, color: 'orange' },
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.02, y: -5 }}
            className="glass-card p-6 relative overflow-hidden"
          >
            <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-${stat.color}-500 to-${stat.color}-400`} />
            <motion.div
              className={`absolute -top-10 -right-10 w-32 h-32 rounded-full bg-${stat.color}-500/20 blur-2xl`}
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 3, repeat: Infinity }}
            />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-xl bg-${stat.color}-500/20`}>
                  <stat.icon className={`w-6 h-6 text-${stat.color}-500`} />
                </div>
                <div className={`flex items-center gap-1 text-sm ${
                  stat.trend === 'up' ? 'text-green-500' : 'text-red-500'
                }`}>
                  {stat.trend === 'up' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {stat.change}
                </div>
              </div>
              <h3 className="text-3xl font-bold">{stat.value}</h3>
              <p className="text-sm text-gray-500 mt-1">{stat.label}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-3 gap-6 mb-6">
        {/* Revenue Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="col-span-2 glass-card p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold">Revenue vs Target</h3>
              <p className="text-sm text-gray-500">Monthly comparison</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={revenueData}>
              <defs>
                <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2D8CFF" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#2D8CFF" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="targetGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#D4AF37" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#E5E7EB'} />
              <XAxis dataKey="month" stroke={isDark ? '#94A3B8' : '#6B7280'} />
              <YAxis stroke={isDark ? '#94A3B8' : '#6B7280'} />
              <Tooltip
                contentStyle={{
                  background: isDark ? '#1E293B' : '#fff',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
                }}
                formatter={(value) => `₹${value.toLocaleString()}`}
              />
              <Area type="monotone" dataKey="revenue" stroke="#2D8CFF" strokeWidth={3} fill="url(#revenueGradient)" name="Revenue" />
              <Area type="monotone" dataKey="target" stroke="#D4AF37" strokeWidth={2} strokeDasharray="5 5" fill="url(#targetGradient)" name="Target" />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Case Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="glass-card p-6"
        >
          <h3 className="text-lg font-semibold mb-2">Case Distribution</h3>
          <p className="text-sm text-gray-500 mb-4">By practice area</p>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={caseDistribution}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {caseDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-2 mt-4">
            {caseDistribution.map((item) => (
              <div key={item.name} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ background: item.color }} />
                <span className="text-xs">{item.name} ({item.value}%)</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Performance Metrics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="glass-card p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold">Performance Metrics</h3>
            <p className="text-sm text-gray-500">Key performance indicators</p>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-6">
          {performanceData.map((metric, index) => (
            <motion.div
              key={metric.metric}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7 + index * 0.1 }}
              className="text-center"
            >
              <div className="relative w-32 h-32 mx-auto mb-4">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke={isDark ? '#334155' : '#E5E7EB'}
                    strokeWidth="8"
                    fill="none"
                  />
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke={metric.value >= metric.target ? '#10B981' : '#F59E0B'}
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray={`${(metric.value / 100) * 352} 352`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl font-bold">{metric.value}%</span>
                </div>
              </div>
              <p className="font-medium">{metric.metric}</p>
              <p className="text-sm text-gray-500">Target: {metric.target}%</p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </MainLayout>
  );
};

export default Analytics;
